import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Check, Home, Sparkles, Building2, ChevronUp, ChevronDown, Info, Plus, Trash2, type LucideIcon } from 'lucide-react';
import { EditableText } from '../EditableText';
import { EditableImage } from '../EditableImage';
import cleanLivingRoom from "@/assets/clean-living-room.jpg";
import cleanKitchen from "@/assets/clean-kitchen.jpg";
import cleanOffice from "@/assets/clean-office.jpg";
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useOrg } from '@/contexts/OrgContext';
import { formatMoney } from '@/lib/currency';
import { useOrgSettings } from '@/hooks/useOrgSettings';

const DEFAULT_IMAGES: Record<string, string> = {
  'Regular Domestic Cleaning': cleanLivingRoom,
  'Airbnb Turnover Cleaning': cleanKitchen,
  'End of Tenancy Cleaning': cleanKitchen,
  'Commercial Property': cleanOffice,
  regular: cleanLivingRoom,
  'one-off': cleanKitchen,
  commercial: cleanOffice,
};

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  'Regular Domestic Cleaning': Home,
  'Airbnb Turnover Cleaning': Sparkles,
  'End of Tenancy Cleaning': Sparkles,
  'Commercial Property': Building2,
};

interface ServiceFromDB {
  id: string;
  name: string;
  description: string | null;
  price: string | number;
  is_quote_only: boolean;
  category: string | null;
}

interface ServiceVariant {
  id: string;
  service_id: string;
  name: string;
  price: string | number;
}

interface ServiceCategory {
  category: string;
  services: ServiceFromDB[];
  variants: ServiceVariant[];
  imageUrl?: string;
  included?: string[];
}

interface CategoryContent {
  imageUrl?: string;
  included?: string[];
}

interface ServicesPageContent {
  hero?: {
    title: string;
    subtitle: string;
    badges?: string[];
  };
  cta?: typeof DEFAULT_CTA;
  categories?: Record<string, CategoryContent>;
  [key: string]: unknown;
}

const DEFAULT_INCLUDED: Record<string, string[]> = {
  'Regular Domestic Cleaning': ['Surface cleaning', 'Vacuuming and mopping', 'Bathroom cleaning', 'Kitchen cleaning', 'Changing bed linen', 'Emptying bins'],
  'Airbnb Turnover Cleaning': ['Complete property clean', 'Fresh linen setup', 'Bathroom sanitisation', 'Kitchen deep clean', 'Welcome staging'],
  'End of Tenancy Cleaning': ['Complete property clean', 'Inside all cupboards', 'Limescale removal', 'Oven and hob clean', 'Carpet cleaning available'],
  'Commercial Property': ['Office and desk cleaning', 'Reception and common areas', 'Kitchen facilities', 'Bathroom sanitisation', 'Floor care', 'Window cleaning'],
};

const DEFAULT_CTA = {
  title: 'Not sure which service you need?',
  subtitle: "Get in touch and we'll help you find the perfect cleaning solution for your home or business.",
};

interface ServicesPageRendererProps {
  content: ServicesPageContent | null;
  onChange: (content: ServicesPageContent) => void;
}

export function ServicesPageRenderer({ content, onChange }: ServicesPageRendererProps) {
  const { orgId } = useOrg();
  const { data: orgSettings } = useOrgSettings(orgId);
  const [servicesFromDB, setServicesFromDB] = useState<ServiceFromDB[]>([]);
  const [variants, setVariants] = useState<ServiceVariant[]>([]);
  const [loading, setLoading] = useState(true);
  
  const data = content || {};
  const hero = data.hero || {
    title: 'Our Cleaning Services',
    subtitle: 'Professional cleaning services across Cardiff & Newport. From regular home cleaning to specialist deep cleans and commercial contracts, we have a service for every need.',
    badges: ['Vetted staff', 'Fully insured', 'Flexible scheduling'],
  };
  const cta = data.cta || DEFAULT_CTA;
  const categoryData = data.categories || {};

  // Fetch services from database
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const [servicesRes, variantsRes] = await Promise.all([
          supabase.from('services').select('id, name, description, price, is_quote_only, category').eq('organization_id', orgId).eq('is_active', true).order('sort_order'),
          supabase.from('service_variants').select('id, service_id, name, price').eq('organization_id', orgId).order('sort_order'),
        ]);
        
        if (servicesRes.data) setServicesFromDB(servicesRes.data);
        if (variantsRes.data) setVariants(variantsRes.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchServices();
  }, [orgId]);

  const update = (section: string, newData: unknown) => {
    onChange({ ...data, [section]: newData });
  };

  const serviceCards: ServiceCategory[] = servicesFromDB.map((service) => {
    const category = service.category || 'Other';
    return {
      category,
      services: [service],
      variants: variants.filter(v => v.service_id === service.id),
      imageUrl: categoryData[category]?.imageUrl || '',
      included: categoryData[category]?.included || DEFAULT_INCLUDED[category] || ['Professional cleaning service'],
    };
  });

  const updateCategoryData = (category: string, updates: Partial<CategoryContent>) => {
    const newCategoryData = { ...categoryData, [category]: { ...categoryData[category], ...updates } };
    update('categories', newCategoryData);
  };

  const moveCategory = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= serviceCards.length) return;
    // Category order comes from DB, we just store the visual preferences
  };

  // Calculate price display for a category
  const getPriceDisplay = (category: ServiceCategory) => {
    // Check if any service in category is quote-only
    const hasQuoteOnly = category.services.some(s => s.is_quote_only);
    if (hasQuoteOnly && category.services.length === 1) {
      return { text: 'Request a Quote', isQuoteOnly: true };
    }
    
    // Get all prices (from services and variants)
    const prices: number[] = [];
    category.services.forEach(service => {
      if (!service.is_quote_only) {
        const servicePrice = parseFloat(String(service.price));
        if (!isNaN(servicePrice) && servicePrice > 0) {
          prices.push(servicePrice);
        }
      }
    });
    category.variants.forEach(variant => {
      const variantPrice = parseFloat(String(variant.price));
      if (!isNaN(variantPrice) && variantPrice > 0) {
        prices.push(variantPrice);
      }
    });
    
    if (prices.length === 0) {
      return { text: 'Request a Quote', isQuoteOnly: true };
    }
    
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    
    // If multiple different prices, show "From" with the organisation currency.
    if (prices.length > 1 || minPrice !== maxPrice) {
      return { text: formatMoney(minPrice, orgSettings?.currency, { from: true }), isQuoteOnly: false };
    }
    
    // Single price, no "From"
    return { text: formatMoney(minPrice, orgSettings?.currency), isQuoteOnly: false };
  };

  // Get CTA text based on category type
  const getCTAText = (category: ServiceCategory) => {
    const hasQuoteOnly = category.services.some(s => s.is_quote_only);
    if (hasQuoteOnly) return 'Request a Quote';
    return 'Book Now';
  };

  if (loading) {
    return <div className="min-h-full bg-background flex items-center justify-center">Loading services...</div>;
  }

  return (
    <TooltipProvider delayDuration={0}>
      <div className="min-h-full bg-background">
        {/* Hero Section */}
        <section className="pt-28 pb-16 bg-background">
          <div className="container mx-auto px-4 text-center">
            <EditableText
              value={hero.title}
              onChange={(v) => update('hero', { ...hero, title: v })}
              as="h1"
              className="text-4xl md:text-5xl font-bold text-foreground mb-4"
            />
            <EditableText
              value={hero.subtitle}
              onChange={(v) => update('hero', { ...hero, subtitle: v })}
              as="p"
              className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8"
            />
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              {hero.badges?.map((badge: string, i: number) => (
                <div key={i} className="flex items-center gap-2 bg-card px-4 py-2 rounded-full border border-border">
                  <Check className="w-4 h-4 text-primary" />
                  <EditableText
                    value={badge}
                    onChange={(v) => {
                      const badges = [...hero.badges];
                      badges[i] = v;
                      update('hero', { ...hero, badges });
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Categories - from DB */}
        <section className="pb-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="space-y-16">
              {serviceCards.map((category, index) => {
                const service = category.services[0];
                const Icon = CATEGORY_ICONS[category.category] || Home;
                const isReversed = index % 2 === 1;
                const priceInfo = getPriceDisplay(category);
                const catData = categoryData[category.category] || {};
                const included = catData.included || DEFAULT_INCLUDED[category.category] || ['Professional cleaning service'];
                
                return (
                  <div 
                    key={service.id} 
                    className="relative group border-2 border-transparent rounded-xl p-4 -m-4 hover:border-primary/30 transition-colors"
                  >
                    {/* Reorder controls */}
                    <div className="absolute -left-2 top-4 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1 z-10">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 bg-background"
                        onClick={() => moveCategory(index, index - 1)}
                        disabled={index === 0}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 bg-background"
                        onClick={() => moveCategory(index, index + 1)}
                        disabled={index === serviceCards.length - 1}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className={cn(
                      "flex flex-col lg:flex-row gap-12 items-start",
                      isReversed && "lg:flex-row-reverse"
                    )}>
                      {/* Image */}
                      <div className="flex-1 w-full">
                        <div className="rounded-3xl overflow-hidden shadow-lg">
                          <EditableImage
                            src={catData.imageUrl || ''}
                            alt={category.category}
                            onChange={(url) => updateCategoryData(category.category, { imageUrl: url })}
                            fallbackSrc={DEFAULT_IMAGES[category.category] || cleanLivingRoom}
                            aspectRatio="16/9"
                          />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 space-y-6">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-7 h-7 text-primary" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                                {service.name}
                              </h2>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button 
                                    className="text-muted-foreground hover:text-primary transition-colors p-1 rounded-full hover:bg-muted"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Info className="w-4 h-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent 
                                  side="top" 
                                  className="max-w-xs z-[100] pointer-events-auto"
                                  sideOffset={8}
                                >
                                  <p className="text-sm">
                                    Service name and description are managed in <strong>Services & Pricing</strong> admin section
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                            <p className="text-muted-foreground mt-1">
                              {category.category}
                            </p>
                            <span className={cn(
                              "text-xl font-semibold mt-2 block",
                              priceInfo.isQuoteOnly ? "text-primary" : "text-primary"
                            )}>
                              {priceInfo.text}
                            </span>
                          </div>
                        </div>

                        {/* Description from first service or category-level */}
                        <p className="text-muted-foreground text-lg leading-relaxed">
                          {service.description || `Professional ${category.category.toLowerCase()} services tailored to your needs.`}
                        </p>

                        {/* What's Included - EDITABLE */}
                        <div className="bg-card rounded-2xl p-6 border border-border">
                          <h3 className="font-semibold text-foreground mb-4">What&apos;s included:</h3>
                          <ul className="grid sm:grid-cols-2 gap-3">
                            {included.map((item: string, itemIndex: number) => (
                              <li key={itemIndex} className="flex items-start gap-2 group/item">
                                <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                                <EditableText
                                  value={item}
                                  onChange={(v) => {
                                    const newIncluded = [...included];
                                    newIncluded[itemIndex] = v;
                                    updateCategoryData(category.category, { included: newIncluded });
                                  }}
                                  className="text-foreground text-sm flex-1"
                                />
                                <button
                                  onClick={() => {
                                    const newIncluded = included.filter((_: string, i: number) => i !== itemIndex);
                                    updateCategoryData(category.category, { included: newIncluded });
                                  }}
                                  className="opacity-0 group-hover/item:opacity-100 p-1 hover:bg-destructive/10 rounded transition-opacity"
                                >
                                  <Trash2 className="w-3 h-3 text-destructive" />
                                </button>
                              </li>
                            ))}
                          </ul>
                          <button
                            onClick={() => {
                              const newIncluded = [...included, 'New item'];
                              updateCategoryData(category.category, { included: newIncluded });
                            }}
                            className="mt-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                            Add item
                          </button>
                        </div>

                        <Button variant="hero" className="pointer-events-none">
                          {getCTAText(category)}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary">
          <div className="container mx-auto px-4 text-center">
            <EditableText
              value={cta.title}
              onChange={(v) => update('cta', { ...cta, title: v })}
              as="h2"
              className="text-3xl font-bold text-primary-foreground mb-4"
            />
            <EditableText
              value={cta.subtitle}
              onChange={(v) => update('cta', { ...cta, subtitle: v })}
              as="p"
              className="text-primary-foreground/80 text-lg max-w-xl mx-auto mb-8"
            />
            <Button size="xl" variant="secondary" className="bg-card text-foreground pointer-events-none">
              Contact us
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>
      </div>
    </TooltipProvider>
  );
}
